import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';
import { IErrorMessage } from '../../components/errorMessage/errorMessage.type';
import InputField from '../../components/InputField';
import { ValidatorRule } from '../../validator/validatorRule.constants';

import styled from 'styled-components';
import Checkbox from '../../components/Checkbox';
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import { zxcvbn } from '@zxcvbn-ts/core';

export const CheckboxWrapper = styled.div`
    margin: 0.25rem auto 0px 0.625rem;
    label {
        font-size: 0.875rem;
    }
`;

interface IPasswordFieldProps {
    autoFocus?: boolean;
    id?: string;
    labelId?: string;
    currentEmail: string;
    currentPassword?: string;
    firstPassword?: string;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    qaId?: string;
    validatorRules?: ValidatorRule[];
    username?: string;
    onChangeShowPassword?: (...args: any[]) => any;
    showPassword?: boolean;
    isNewPasswordFocused?: boolean;
    onNewPasswordFocusChange?: (isFocused: boolean) => void;
    onValidationStateChange?: (validationState: PasswordValidationState) => void;
}

export interface PasswordValidationState {
    hasWeakPassword: boolean;
    hasInvalidPassword: boolean;
    hasMismatch: boolean;
    isOldPassword: boolean;
    isEmailOrUsername: boolean;
    hasWrongLength: boolean;
    hasInvalidChars: boolean;
}

const INVALID = 'invalid';
const VALID = 'valid';

export interface IPasswordFieldState {
    errors: { [key: string]: { message: IErrorMessage; validity: string } };
    passwordStrength: PasswordScoreType;
    passwordMessages: string[];
}

export const PasswordScore = {
    Weak: 'weak',
    Medium: 'medium',
    Strong: 'strong',
} as const;

export type PasswordScoreType = typeof PasswordScore[keyof typeof PasswordScore];

export const scoreColors = {
    [PasswordScore.Weak]: '#DF0101',
    [PasswordScore.Medium]: '#F16A00',
    [PasswordScore.Strong]: '#047205',
};

const MAX_LENGTH = 128;
const MIN_LENGTH = 8;

const PasswordStrengthWrapper = styled.div`
    margin-top: 10px;
    font-size: 0.875rem;

    .strength-bar {
        display: flex;
        height: 8px;
        margin-top: 5px;
        margin-bottom: 5px;

        & > div {
            flex: 1;
            margin-right: 2px;
            background-color: #e0e0e0;

            border-radius: 40px;

            &:last-child {
                margin-right: 0;
            }
        }

        &.weak div:nth-child(-n + 1) {
            background-color: #df0101;
        }

        &.medium div:nth-child(-n + 2) {
            background-color: #f16a00;
        }

        &.strong div:nth-child(-n + 3) {
            background-color: #047205;
        }
    }
`;

const StyledErrorMessage = styled.div<{ color: string }>`
    .error {
        color: ${(props) => props.color};
    }
`;

class NewPasswordField extends React.Component<IPasswordFieldProps, IPasswordFieldState> {
    private readonly ID: string;
    private readonly LABEL_ID: string;

    constructor(props: IPasswordFieldProps) {
        super(props);
        const { id, labelId } = this.props;

        this.ID = id ? id : 'password';
        this.LABEL_ID = labelId ? labelId : 'label.password';
        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);

        this.state = {
            errors: {
                isOld: {
                    message: TranslationHelpers.getMessageDescriptor('error.new_password.no_old'),
                    validity: VALID,
                },
                isEmail: {
                    message: TranslationHelpers.getMessageDescriptor('error.new_password.no_email'),
                    validity: VALID,
                },
                misMatch: {
                    message: TranslationHelpers.getMessageDescriptor('error.password.mismatch'),
                    validity: VALID,
                },
                wrongLength: {
                    message: TranslationHelpers.getFormattedMessageProps(
                        'error.password.outside_boundaries',
                        { min: MIN_LENGTH },
                    ),
                    validity: VALID,
                },
                haveSpaceOrSpecialASII: {
                    message: TranslationHelpers.getMessageDescriptor('error.illegal_chars'),
                    validity: VALID,
                },
            },
            passwordStrength: PasswordScore.Weak,
            passwordMessages: [],
        };
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): IErrorMessage {
        const { currentEmail, currentPassword, firstPassword, username, onValidationStateChange } =
            this.props;

        const errors = { ...this.state.errors };

        const { score: passwordStrength, messages: passwordMessages } = this.analyzePassword(
            value || '',
        );

        this.setState({ passwordStrength, passwordMessages });

        if (this.ID === 'password') {
            if (value) {
                if (currentPassword && value === currentPassword) {
                    errors.isOld.validity = INVALID;
                } else {
                    errors.isOld.validity = VALID;
                }
                if (
                    value.toLocaleLowerCase() === currentEmail.toLocaleLowerCase() ||
                    (username && value.toLocaleLowerCase() === username.toLocaleLowerCase())
                ) {
                    errors.isEmail.validity = INVALID;
                } else {
                    errors.isEmail.validity = VALID;
                }

                if (/^[!-~]+$/.test(value)) {
                    errors.haveSpaceOrSpecialASII.validity = VALID;
                } else {
                    errors.haveSpaceOrSpecialASII.validity = INVALID;
                }
                this.setState({ errors: errors });
            }
            if (value === '') {
                errors.isOld.validity = VALID;
                errors.isEmail.validity = VALID;
                errors.haveSpaceOrSpecialASII.validity = VALID;
            }
            if (value.length < MIN_LENGTH || value.length > MAX_LENGTH) {
                errors.wrongLength.validity = INVALID;
            } else {
                errors.wrongLength.validity = VALID;
            }
        } else {
            if (value !== firstPassword) {
                errors.misMatch.validity = INVALID;
            } else {
                errors.misMatch.validity = VALID;
            }
            this.setState({ errors });
        }
        const hasError = Object.keys(errors).some((v) => errors[v].validity === INVALID);
        hasError && this.setState({ passwordStrength: PasswordScore.Weak });

        // Report validation state to parent
        if (onValidationStateChange && this.ID === 'password') {
            const validationState: PasswordValidationState = {
                hasWeakPassword: passwordStrength === PasswordScore.Weak,
                hasInvalidPassword:
                    errors.isOld.validity === INVALID ||
                    errors.isEmail.validity === INVALID ||
                    errors.wrongLength.validity === INVALID ||
                    errors.haveSpaceOrSpecialASII.validity === INVALID,
                hasMismatch: false,
                isOldPassword: errors.isOld.validity === INVALID,
                isEmailOrUsername: errors.isEmail.validity === INVALID,
                hasWrongLength: errors.wrongLength.validity === INVALID,
                hasInvalidChars: errors.haveSpaceOrSpecialASII.validity === INVALID,
            };
            onValidationStateChange(validationState);
        }

        if (onValidationStateChange && this.ID === 'repeatPassword') {
            const validationState: PasswordValidationState = {
                hasWeakPassword: false,
                hasInvalidPassword: false,
                hasMismatch: errors.misMatch.validity === INVALID,
                isOldPassword: false,
                isEmailOrUsername: false,
                hasWrongLength: false,
                hasInvalidChars: false,
            };
            onValidationStateChange(validationState);
        }

        return hasError || passwordStrength === PasswordScore.Weak ? INVALID : '';
    }

    private renderValidation() {
        const messages = Object.keys(this.state.errors);
        return messages.map((v) => {
            return this.state.errors[v].validity === INVALID ? (
                <ErrorMessage message={this.state.errors[v].message} key={v} />
            ) : null;
        });
    }

    private renderValidationForZxcvbn(messages: string[], score: PasswordScoreType) {
        return messages.map((message: string) => {
            return (
                <StyledErrorMessage key={message} color={scoreColors[score]}>
                    <ErrorMessage message={message} />
                </StyledErrorMessage>
            );
        });
    }

    private analyzePassword(password: string): { score: PasswordScoreType; messages: string[] } {
        const result = zxcvbn(password);
        const messages = [];
        if (result.feedback.warning) {
            messages.push(result.feedback.warning);
        }
        if (result.feedback.suggestions.length > 0) {
            messages.push(...result.feedback.suggestions);
        }
        const filterMessages = (messages: string[]): string[] => {
            return Array.from(new Set(messages)).filter((message) => message.trim() !== '');
        };
        const filteredMessages = filterMessages(messages);
        const resultScore = this.getStrengthType(result.score);
        return { score: resultScore, messages: filteredMessages };
    }

    private getStrengthType(score: number): PasswordScoreType {
        if (score > 3) return PasswordScore.Strong;
        if (score === 2 || score === 3) return PasswordScore.Medium;
        return PasswordScore.Weak;
    }

    private renderInputField({ form: { touched, isSubmitting } }: FieldProps): ReactNode {
        const {
            autoFocus,
            value,
            required,
            qaId,
            onChangeShowPassword,
            showPassword,
            intl,
            id,
            onNewPasswordFocusChange,
            isNewPasswordFocused,
        } = this.props;

        const isTouched = touched && touched[this.ID];
        const hasError = Object.keys(this.state.errors).some(
            (v) => this.state.errors[v].validity === INVALID,
        );
        const isFirstInput = id === 'password';
        const { passwordStrength, passwordMessages } = this.state;
        const showPasswordScore = isFirstInput && (isNewPasswordFocused || isTouched);

        const handleNewPasswordBlur = () => {
            if (onNewPasswordFocusChange) {
                onNewPasswordFocusChange(false);
            }
        };
        const handleNewPasswordFocus = () => {
            if (onNewPasswordFocusChange) {
                onNewPasswordFocusChange(true);
            }
        };

        let isValid;
        if (value && !hasError) {
            isValid = true;
        }
        if (value && hasError) {
            isValid = false;
        }
        if (isTouched && !value && required) {
            isValid = false;
        }

        const newPwdCheckBoxAriaLabel = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('label.show_new_password'),
        );

        if (isSubmitting && passwordStrength !== PasswordScore.Weak) {
            this.setState({ passwordStrength: PasswordScore.Weak });
        }

        if (isTouched && passwordStrength === PasswordScore.Weak) {
            isValid = false;
        }
        const passwordScoreText = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('password.score'),
        );
        const passwordScoreTextWeak = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('password.score.weak'),
        );
        const passwordScoreTextMedium = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('password.score.medium'),
        );
        const passwordScoreTextStrong = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('password.score.strong'),
        );

        const showZxcvbnMessages =
            (isValid !== undefined || isNewPasswordFocused) &&
            passwordMessages.length > 0 &&
            isFirstInput;
        const showValidationMessages = isValid !== undefined || isTouched || isNewPasswordFocused;

        return (
            <div>
                <InputField
                    autoComplete="new-password"
                    labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                    type={showPassword ? 'text' : 'password'}
                    id={this.ID}
                    name={this.ID}
                    value={value}
                    autoFocus={autoFocus}
                    required={required}
                    maxLength={MAX_LENGTH}
                    convert={false}
                    data-qa-id={`${qaId ? qaId : 'new-password'}-field`}
                    valid={isValid}
                    onNewPasswordBlur={handleNewPasswordBlur}
                    onNewPasswordFocus={handleNewPasswordFocus}
                >
                    {showPasswordScore && (
                        <PasswordStrengthWrapper>
                            <div className={`strength-bar ${passwordStrength}`}>
                                <div />
                                <div />
                                <div />
                            </div>
                            <span style={{ color: ' #686868' }}>
                                {passwordScoreText}
                                <span
                                    style={{
                                        color: scoreColors[passwordStrength],
                                    }}
                                >
                                    {passwordStrength === PasswordScore.Weak &&
                                        passwordScoreTextWeak}
                                    {passwordStrength === PasswordScore.Medium &&
                                        passwordScoreTextMedium}
                                    {passwordStrength === PasswordScore.Strong &&
                                        passwordScoreTextStrong}
                                </span>
                            </span>
                        </PasswordStrengthWrapper>
                    )}

                    {onChangeShowPassword && (
                        <CheckboxWrapper>
                            <Checkbox
                                id="showNewPassword"
                                name="showNewPassword"
                                ariaLabel={newPwdCheckBoxAriaLabel}
                                label="label.show_new_password"
                                value={showPassword}
                                onChange={onChangeShowPassword}
                                tooltipText={false}
                            />
                        </CheckboxWrapper>
                    )}
                    {showValidationMessages && this.renderValidation()}
                    {showZxcvbnMessages &&
                        this.renderValidationForZxcvbn(passwordMessages, passwordStrength)}
                </InputField>
            </div>
        );
    }
}

export default injectIntl(NewPasswordField);
